import React, { useState } from "react";
import { Button, Row, Col, Form, Card, Badge, Table, Spinner } from "react-bootstrap";
import Select from "react-select";
import { useLazyGetUserBonusListQuery, useGetAllUsersListQuery } from "../../../redux/userApi";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { toast } from "react-toastify";

function UserBonusList() {
    const { t } = useTranslation();
    const [selectedUserId, setSelectedUserId] = useState("");

    const [triggerFetch, { data: apiResponse, isLoading, isFetching }] = useLazyGetUserBonusListQuery();
    const { data: userResponse } = useGetAllUsersListQuery();
    const users = userResponse?.data || [];

    const userOptions = [
        { value: "", label: t('select_user') || "Select User" },
        ...users.map(user => ({
            value: user.id,
            label: String(user.mobile ? `${user.mobile} (${user.fname || ''} ${user.lname || ''})`.trim() : (user.email || user.id))
        }))
    ];

    const customStyles = {
        control: (provided) => ({ ...provided, color: '#000' }),
        singleValue: (provided) => ({ ...provided, color: '#000' }),
        input: (provided) => ({ ...provided, color: '#000' }),
        option: (provided, state) => ({
            ...provided,
            color: '#000',
            backgroundColor: state.isFocused ? '#e9ecef' : '#fff',
        }),
        menu: (provided) => ({ ...provided, backgroundColor: '#fff', zIndex: 9999 })
    };

    const handleUserSelect = (selectedOption) => {
        setSelectedUserId(selectedOption ? selectedOption.value : "");
    };

    const handleSearchSubmit = (e) => {
        if (e) e.preventDefault();
        if (!selectedUserId) {
            toast.error(t("please_select_user") || "Please select a user");
            return;
        }
        triggerFetch({ mobile: selectedUserId });
    };

    const handleClear = () => {
        setSelectedUserId("");
    };

    const responseData = apiResponse?.data;
    const userInfo = responseData?.user_info;
    const claimedList = responseData?.userBonusList || [];
    const totalBonus = responseData?.user_total_bonus || 0;
    const showPopup = responseData?.show_daily_bonus_popup;
    const plans = responseData?.daily_bonus_plans || [];

    // Map claimed bonus dates by day number
    const claimedMap = {};
    claimedList.forEach((item) => {
        claimedMap[item.bonus_day] = item.bonus_date;
    });

    return (
        <section className="section dashboard">
            <div className="pagetitle d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h1>{t("user_bonus_status") || "User Bonus Status"}</h1>
                    <p className="text-muted mb-0">
                        {t("search_user_bonus_desc") || "Select a user to view daily bonus list and status"}
                    </p>
                </div>
            </div>

            {/* Search Card */}
            <Card className="mb-4 shadow-sm border-0">
                <Card.Body className="pt-4">
                    <Form onSubmit={handleSearchSubmit}>
                        <Row className="align-items-end g-3">
                            <Col md={6} lg={5}>
                                <Form.Group controlId="filterUserId">
                                    <Form.Label className="fw-bold">
                                        {t("select_user") || "Select User (Mobile / Name)"}
                                    </Form.Label>
                                    <Select
                                        options={userOptions}
                                        value={userOptions.find(opt => String(opt.value) === String(selectedUserId)) || userOptions[0]}
                                        onChange={handleUserSelect}
                                        isSearchable
                                        styles={customStyles}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4} className="d-flex gap-2">
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={isLoading || isFetching}
                                    className="px-4 py-2 d-flex align-items-center gap-2"
                                >
                                    {(isLoading || isFetching) ? (
                                        <>
                                            <Spinner animation="border" size="sm" />
                                            <span>{t("searching") || "Searching..."}</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-search"></i>
                                            <span>{t("search") || "Submit"}</span>
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    onClick={handleClear}
                                    className="px-3 py-2"
                                >
                                    {t("clear") || "Clear"}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Error or Not Found State */}
            {apiResponse?.status === false && (
                <Card className="border-danger mb-4">
                    <Card.Body className="text-danger fw-bold text-center py-4">
                        <i className="bi bi-exclamation-circle fs-3 d-block mb-2"></i>
                        {apiResponse?.message === "USER_NOT_FOUND"
                            ? (t("user_not_found") || "User Not Found")
                            : apiResponse?.message}
                    </Card.Body>
                </Card>
            )}

            {/* Response Data Display */}
            {responseData && (
                <>
                    {/* User & Bonus Summary Cards */}
                    <Row className="g-3 mb-4">
                        <Col md={6} lg={3}>
                            <Card className="h-100 border-0 shadow-sm bg-primary text-white">
                                <Card.Body className="d-flex align-items-center gap-3">
                                    <div className="fs-1 bg-white bg-opacity-25 rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 60, height: 60 }}>
                                        <i className="bi bi-person-fill text-white"></i>
                                    </div>
                                    <div>
                                        <div className="text-white-50 small fw-bold">{t("user_details") || "User Details"}</div>
                                        <div className="fs-6 fw-bold">{userInfo?.fname} {userInfo?.lname}</div>
                                        <div className="small">{userInfo?.mobile || userInfo?.email || `ID: ${userInfo?.user_id}`}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6} lg={3}>
                            <Card className="h-100 border-0 shadow-sm bg-success text-white">
                                <Card.Body className="d-flex align-items-center gap-3">
                                    <div className="fs-1 bg-white bg-opacity-25 rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 60, height: 60 }}>
                                        <i className="bi bi-gift-fill text-white"></i>
                                    </div>
                                    <div>
                                        <div className="text-white-50 small fw-bold">{t("total_bonus_points") || "Total Bonus Points"}</div>
                                        <div className="fs-4 fw-bold">{totalBonus}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6} lg={3}>
                            <Card className="h-100 border-0 shadow-sm bg-info text-white">
                                <Card.Body className="d-flex align-items-center gap-3">
                                    <div className="fs-1 bg-white bg-opacity-25 rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 60, height: 60 }}>
                                        <i className="bi bi-window text-white"></i>
                                    </div>
                                    <div>
                                        <div className="text-white-50 small fw-bold">{t("show_daily_popup") || "Show Daily Popup"}</div>
                                        <div className="fs-5 fw-bold">
                                            {showPopup ? (
                                                <Badge bg="light" text="dark">{t("yes") || "YES (Pending Claim)"}</Badge>
                                            ) : (
                                                <Badge bg="dark">{t("no") || "NO (Claimed Today)"}</Badge>
                                            )}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6} lg={3}>
                            <Card className="h-100 border-0 shadow-sm bg-warning text-dark">
                                <Card.Body className="d-flex align-items-center gap-3">
                                    <div className="fs-1 bg-dark bg-opacity-10 rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 60, height: 60 }}>
                                        <i className="bi bi-phone"></i>
                                    </div>
                                    <div>
                                        <div className="text-muted small fw-bold">{t("device_id") || "Device ID"}</div>
                                        <div className="small fw-bold text-truncate" style={{ maxWidth: 160 }} title={userInfo?.deviceid}>
                                            {userInfo?.deviceid || "N/A"}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Daily Bonus Days Table */}
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white py-3 border-0 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 fw-bold">{t("daily_bonus_days_status") || "Daily Bonus Days Status"}</h5>
                            <Badge bg="secondary">
                                {claimedList.length} / {plans.length || 30} {t("days_claimed") || "Days Claimed"}
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="px-4 py-3">{t("day") || "Day"}</th>
                                        <th className="py-3">{t("bonus_amount") || "Bonus Amount"}</th>
                                        <th className="py-3">{t("status") || "Status"}</th>
                                        <th className="py-3">{t("claimed_date") || "Claimed Date"}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(plans.length > 0 ? plans : Array.from({ length: 30 }, (_, i) => ({ day: i + 1, bonus_amount: "-" }))).map((plan) => {
                                        const dayNum = plan.day;
                                        const isClaimed = !!claimedMap[dayNum];
                                        const claimedDate = claimedMap[dayNum];

                                        return (
                                            <tr key={`day-${dayNum}`}>
                                                <td className="px-4 fw-bold">
                                                    <i className="bi bi-calendar-check me-2 text-primary"></i>
                                                    {t("day") || "Day"} {dayNum}
                                                </td>
                                                <td>
                                                    <span className="fw-bold text-success">
                                                        {plan.bonus_amount}
                                                    </span>
                                                </td>
                                                <td>
                                                    {isClaimed ? (
                                                        <Badge bg="success" className="px-3 py-2">
                                                            <i className="bi bi-check-circle-fill me-1"></i>
                                                            {t("claimed") || "Claimed"}
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="secondary" className="px-3 py-2">
                                                            {t("pending") || "Pending"}
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    {claimedDate ? (
                                                        <span className="text-dark fw-medium">
                                                            {moment(claimedDate).format("DD/MM/YYYY")}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </>
            )}
        </section>
    );
}

export default UserBonusList;
